/**
 * WebAuthn (passkey) helpers.
 *
 * The server speaks py_webauthn's JSON dialect: every binary field arrives as
 * a base64url string, and the browser API needs ArrayBuffers. The conversion
 * is done by hand rather than through `PublicKeyCredential.parseCreationOptionsFromJSON`
 * because that method is still missing on Safari versions patients are using.
 *
 * No biometric data ever reaches the server — only a public key credential.
 */

export class PasskeyError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "PasskeyError";
    this.code = code;
  }
}

function base64urlToBuffer(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), "="));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Passkeys need a secure context; http:// beyond localhost silently fails. */
export function passkeySupported(): boolean {
  return (
    typeof window !== "undefined" &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential === "function"
  );
}

type CredentialDescriptorJSON = { id: string; type?: string; transports?: string[] };

function toDescriptors(list: CredentialDescriptorJSON[] | undefined) {
  return (list ?? []).map((item) => ({
    id: base64urlToBuffer(item.id),
    type: "public-key" as const,
    transports: item.transports as AuthenticatorTransport[] | undefined,
  }));
}

/** Shape the browser returns, serialised the way the server expects it back. */
function serialiseCredential(credential: PublicKeyCredential) {
  const response = credential.response as
    | AuthenticatorAttestationResponse
    | AuthenticatorAssertionResponse;

  const payload: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    } as Record<string, unknown>,
  };
  const inner = payload.response as Record<string, unknown>;

  if ("attestationObject" in response) {
    inner.attestationObject = bufferToBase64url(response.attestationObject);
    // py_webauthn reads transports from here when storing the credential.
    inner.transports =
      typeof response.getTransports === "function" ? response.getTransports() : [];
  } else {
    inner.authenticatorData = bufferToBase64url(response.authenticatorData);
    inner.signature = bufferToBase64url(response.signature);
    inner.userHandle = response.userHandle ? bufferToBase64url(response.userHandle) : null;
  }

  return payload;
}

function translate(cause: unknown): PasskeyError {
  if (cause instanceof PasskeyError) return cause;
  if (cause instanceof DOMException) {
    // The browser deliberately does not say *why* it failed, so these messages
    // describe what the user can do rather than guessing the cause.
    if (cause.name === "NotAllowedError") {
      return new PasskeyError(
        "cancelled",
        "Verifikasi dibatalkan atau melewati batas waktu. Coba lagi.",
      );
    }
    if (cause.name === "InvalidStateError") {
      return new PasskeyError(
        "already_registered",
        "Perangkat ini sudah terdaftar sebagai passkey untuk akun Anda.",
      );
    }
    if (cause.name === "SecurityError") {
      return new PasskeyError(
        "insecure_origin",
        "Passkey memerlukan koneksi HTTPS dengan domain yang terdaftar.",
      );
    }
  }
  return new PasskeyError("failed", "Passkey tidak dapat diproses pada perangkat ini.");
}

/** Creates a passkey for the signed-in patient. */
export async function createPasskey(
  options: PublicKeyCredentialCreationOptionsJSON,
): Promise<Record<string, unknown>> {
  if (!passkeySupported()) {
    throw new PasskeyError("unsupported", "Perangkat atau browser ini tidak mendukung passkey.");
  }
  try {
    const credential = (await navigator.credentials.create({
      publicKey: {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        user: { ...options.user, id: base64urlToBuffer(options.user.id) },
        excludeCredentials: toDescriptors(options.excludeCredentials),
      } as PublicKeyCredentialCreationOptions,
    })) as PublicKeyCredential | null;

    if (!credential) throw new PasskeyError("cancelled", "Pembuatan passkey dibatalkan.");
    return serialiseCredential(credential);
  } catch (cause) {
    throw translate(cause);
  }
}

/** Signs in with an existing passkey. */
export async function usePasskey(
  options: PublicKeyCredentialRequestOptionsJSON,
): Promise<Record<string, unknown>> {
  if (!passkeySupported()) {
    throw new PasskeyError("unsupported", "Perangkat atau browser ini tidak mendukung passkey.");
  }
  try {
    const credential = (await navigator.credentials.get({
      publicKey: {
        ...options,
        challenge: base64urlToBuffer(options.challenge),
        allowCredentials: toDescriptors(options.allowCredentials),
      } as PublicKeyCredentialRequestOptions,
    })) as PublicKeyCredential | null;

    if (!credential) throw new PasskeyError("cancelled", "Verifikasi dibatalkan.");
    return serialiseCredential(credential);
  } catch (cause) {
    throw translate(cause);
  }
}

// --- JSON option shapes returned by py_webauthn -----------------------------

export type PublicKeyCredentialCreationOptionsJSON = {
  challenge: string;
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { alg: number; type: "public-key" }[];
  timeout?: number;
  excludeCredentials?: CredentialDescriptorJSON[];
  authenticatorSelection?: Record<string, unknown>;
  attestation?: string;
};

export type PublicKeyCredentialRequestOptionsJSON = {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials?: CredentialDescriptorJSON[];
  userVerification?: string;
};
